import React, { useState } from "react";
import i18n from "@/i18n";

import { ItemList, ItemListColumn, ItemListRow } from "../itemList";

import useSendEmailJobs from "@/hooks/notifications/useSendEmailJobs";
import useSendEmailJob from "@/hooks/notifications/useSendEmailJob";
import useDialogModal from "@/hooks/useDialogModal";
import { Badge } from "../ui/badge";
import DialogModal from "../dialogModal";
import ToolbarMenu from "../toolbarMenu";
import { Button } from "../ui/button";
import {
  Calendar,
  Eye,
  EyeClosed,
  Mail,
  Paperclip,
  Pen,
  RefreshCcw,
  User,
  X,
} from "lucide-react";
import Breadcrumbs from "../breadcrumbs";
import { cn } from "../utils";
import useRestrictAccess from "@/hooks/subscriptions/useRestrictAccess";

const { t } = i18n;

const SendEmailJobStatusBadge = ({
  status = "pending",
  failureReason,
  ...props
}) => {
  const label = t(`send_email_job.send_grid.status.${status}`);

  const variant = {
    pending: "outline",
    processed: "outline-primary",
    processing: "outline-primary",
    delivered: "outline-success",
    not_delivered: "outline-destructive",
    failed: "outline-destructive",
  }[status];

  let title;

  if (status === "not_delivered" || status === "failed") {
    title =
      {
        dropped: t("send_email_jobs.failure_reasons.descriptions.dropped"),
      }[failureReason] ||
      t("send_email_jobs.failure_reasons.descriptions.unknown");
  }

  return (
    <Badge variant={variant} title={title} {...props}>
      {label}
    </Badge>
  );
};

const SendEmailJobsList = ({ filterByUserCommId, onClearFilter }) => {
  const { dialogElement } = useDialogModal();

  const [sorting, setSorting] = useState(["createdAt", true]);

  const { hasAccess, restrict, restrictDialogElement } = useRestrictAccess();
  const hasOpensTracking = hasAccess("communication-opened-tracking");

  const {
    data: sendEmailJobsQuery,
    refetch: refetchSendEmailJobs,
    isFetching: isFetchingSendEmailJobs,
  } = useSendEmailJobs(filterByUserCommId);
  const sendEmailJobs = sendEmailJobsQuery?.data;

  const [selectedSendEmailJobId, setSelectedSendEmailJobId] = useState(null);
  const {
    data: selectedSendEmailJobQuery,
    isLoading: isLoadingSelectedSendEmailJob,
  } = useSendEmailJob(selectedSendEmailJobId);
  const selectedSendEmailJob = selectedSendEmailJobQuery?.data;

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <Breadcrumbs items={[{ label: t("send_email_jobs.title") }]} />
        <ToolbarMenu>
          {filterByUserCommId && (
            <Button onClick={() => onClearFilter?.()}>
              <X /> {t("generic.clear_filter")}
            </Button>
          )}
          <Button
            variant="secondary"
            isLoading={isFetchingSendEmailJobs}
            onClick={async () => {
              await refetchSendEmailJobs();
            }}
          >
            <RefreshCcw /> {t("generic.refresh")}
          </Button>
        </ToolbarMenu>
      </div>
      <div
        className={cn("max-h-full overflow-hidden", {
          "rounded-xl border-2 border-primary": filterByUserCommId,
        })}
      >
        <ItemList
          columns={[
            {
              id: "createdAt",
              label: t("send_email_job.sent_at"),
              isSortable: true,
            },
            { id: "recipientName", label: t("name"), isSortable: true },
            { id: "email", label: t("generic.email"), isSortable: true },
            {
              id: "opensCount",
              label: (
                <div className="flex items-center gap-1">
                  {t("send_email_jobs.opens")}
                </div>
              ),
              isSortable: hasOpensTracking,
            },
            { id: "status", label: t("generic.status"), isSortable: true },
          ]}
          columnSizing="100px 1fr 150px 45px 90px"
          rightAlignLastColumn
          zeroStateRenderer={() => t("send_email_jobs.list.zero_state")}
          sorting={sorting}
          onSort={setSorting}
        >
          {sendEmailJobs
            ?.toSorted((a, b) => {
              const [sortBy, sortDesc] = sorting;
              const x = sortDesc ? b : a;
              const y = sortDesc ? a : b;
              switch (sortBy) {
                case "recipientName":
                case "email":
                case "subject":
                  return x[sortBy]?.localeCompare(y[sortBy]);
                case "opensCount":
                  return (
                    (x.sendGrid.opensCount ?? 0) - (y.sendGrid.opensCount ?? 0)
                  );

                case "status":
                  return x.sendGrid?.status?.localeCompare(y.sendGrid?.status);
                case "createdAt":
                  return new Date(x.createdAt) - new Date(y.createdAt);
              }
              return 0;
            })
            ?.map((sendEmailJob) => (
              <ItemListRow
                key={sendEmailJob.id}
                onClick={() => setSelectedSendEmailJobId(sendEmailJob.id)}
              >
                <ItemListColumn.Date>
                  {sendEmailJob.createdAt}
                </ItemListColumn.Date>
                <ItemListColumn truncate>
                  {sendEmailJob.recipientName ?? "-"}
                </ItemListColumn>
                <ItemListColumn truncate>{sendEmailJob.email}</ItemListColumn>

                <ItemListColumn className="overflow-hidden">
                  {hasOpensTracking ? (
                    <Button
                      variant="ghost"
                      className="p-0"
                      title={t("send_email_jobs.opens.tooltip", {
                        count: sendEmailJob.sendGrid?.opensCount ?? 0,
                        name: sendEmailJob.recipientName,
                      })}
                    >
                      {sendEmailJob.sendGrid?.opensCount ? (
                        <>
                          <Eye size={12} className="text-slate-400" />
                          {sendEmailJob.sendGrid?.opensCount}
                        </>
                      ) : (
                        <EyeClosed size={12} className="text-slate-400" />
                      )}
                    </Button>
                  ) : (
                    restrict(
                      <Button size="sm" variant="ghost">
                        <div className="flex items-center gap-1 font-mono blur-[3px]">
                          {sendEmailJob.id.charAt(0)}
                        </div>
                      </Button>,
                      {
                        requiredFeature: "communication-opened-tracking",
                      },
                    )
                  )}
                </ItemListColumn>
                <ItemListColumn>
                  <SendEmailJobStatusBadge
                    status={sendEmailJob.sendGrid?.status}
                    failureReason={sendEmailJob.sendGrid?.failureReason}
                    size="sm"
                  />
                </ItemListColumn>
              </ItemListRow>
            ))}
        </ItemList>
      </div>
      <DialogModal
        open={selectedSendEmailJobId}
        size="lg"
        className="to-slate-transparent bg-gradient-to-b from-slate-200"
        isLoading={isLoadingSelectedSendEmailJob}
        onCancel={() => setSelectedSendEmailJobId(null)}
        submitText={null}
        title={
          selectedSendEmailJob ? (
            <SendEmailJobStatusBadge
              status={selectedSendEmailJob?.sendGrid?.status}
              failureReason={selectedSendEmailJob.sendGrid?.failureReason}
            />
          ) : (
            "..."
          )
        }
        bodyRenderer={() => (
          <div className="mx-auto flex w-full max-w-[800px] flex-col gap-4">
            <div className="flex flex-wrap gap-4 [&>div]:flex [&>div]:items-center [&>div]:gap-2 [&>div]:rounded-lg [&>div]:bg-background [&>div]:p-2 [&>div]:shadow-lg">
              <div>
                <User size={12} /> {selectedSendEmailJob?.recipientName}
              </div>
              <div>
                <Mail size={12} /> {selectedSendEmailJob?.email}
              </div>
              <div>
                <Pen size={12} /> {selectedSendEmailJob?.subject}
              </div>
              <div>
                <Calendar size={12} /> {selectedSendEmailJob?.createdAt}
              </div>
              {selectedSendEmailJob?.attachments?.map?.((attachment) => (
                <div key={attachment.id}>
                  <Paperclip size={12} /> {attachment.filename}
                </div>
              ))}

              {hasOpensTracking ? (
                <div>
                  <Eye size={12} />{" "}
                  {selectedSendEmailJob?.sendGrid?.opensCount ?? 0}
                </div>
              ) : (
                <div>
                  {restrict(
                    <Button
                      size="sm"
                      variant="ghost"
                      title={t("send_email_jobs.opens")}
                    >
                      <Eye size={12} />
                      <span className="blur-[3px]">X</span>
                    </Button>,
                    { requiredFeature: "communication-opened-tracking" },
                  )}
                </div>
              )}
            </div>
            <div
              className="tiptap rounded-lg shadow-lg"
              dangerouslySetInnerHTML={{
                __html: selectedSendEmailJob?.body,
              }}
            />
          </div>
        )}
      />
      {restrictDialogElement}
      {dialogElement}
    </div>
  );
};

export default SendEmailJobsList;
